import { supabase } from "@/integrations/supabase/client";

/**
 * Ensures a valid authenticated session exists before making API calls.
 * Refreshes the session if the token is near expiry.
 * 
 * @returns {Promise<boolean>} - true if a valid session exists or was refreshed
 */
export async function ensureAuthenticatedSession(): Promise<boolean> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.warn('[authRetry] getSession error:', error.message);
      // Don't block on transient errors - let the request proceed and fail naturally
      return true;
    }
    
    if (!session?.access_token) {
      console.debug('[authRetry] No valid session/token');
      return false;
    }
    
    // Force refresh if token is near expiry (within 60 seconds)
    const expiresAt = session.expires_at;
    if (expiresAt && Date.now() / 1000 > expiresAt - 60) {
      console.debug('[authRetry] Token near expiry, refreshing...');
      const { data, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        console.warn('[authRetry] Session refresh failed:', refreshError.message);
        // Don't block - the token might still be valid for a few more seconds
        return true;
      }
      // Verify refresh worked
      if (!data.session?.access_token) {
        console.debug('[authRetry] Refresh succeeded but no token');
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error('[authRetry] Exception:', error);
    // Don't block on exceptions - let the request proceed
    return true;
  }
}

/**
 * Wraps an async function with automatic retry logic for auth failures.
 * Refreshes the session on 401/403/P0001 errors and retries.
 * 
 * @param fn - The async function to execute
 * @param maxRetries - Maximum number of retry attempts (default: 2)
 * @returns The result of the function
 */
export async function withAuthRetry<T>(
  fn: () => Promise<T>,
  maxRetries = 2
): Promise<T> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string; status?: number };
      const isAuthError = 
        err?.code === 'P0001' || 
        err?.message?.includes('Access denied') ||
        err?.status === 401 ||
        err?.status === 403;
      
      if (isAuthError && attempt < maxRetries) {
        console.debug(`[authRetry] Auth error on attempt ${attempt + 1}, refreshing session...`);
        await supabase.auth.refreshSession();
        // Short delay before retry
        await new Promise(r => setTimeout(r, 200 * (attempt + 1)));
        continue;
      }
      
      throw error;
    }
  }
  
  // This should never be reached, but TypeScript needs it
  throw new Error('Max retries exceeded');
}
